import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { getSingleStoreConfig, setRestaurantSlug } from "../../../services/singlestore/actions";
import { detectRestaurantSlug, storeRestaurantSlug } from "../../../helpers/restaurantSlugDetector";
import Loading from "../../helpers/loading";

class SingleStoreRouter extends Component {
	state = {
		initialLoadComplete: false,
	};

	componentDidMount() {
		this.checkAndLoadRestaurant();
		
		// Add event listeners for location changes
		window.addEventListener('locationChanged', this.handleLocationChange);
		window.addEventListener('storage', this.handleStorageChange);
		
		// Store current location for change detection
		const userSetAddress = localStorage.getItem('userSetAddress');
		if (userSetAddress) {
			try {
				this.currentLocation = JSON.parse(userSetAddress);
			} catch (e) {
				this.currentLocation = null;
			}
		}
	}

	componentDidUpdate(prevProps) {
		// Only reload brand config if the brand slug actually changed
		const prevSlug = this.getSlugFromPath(prevProps.location.pathname);
		const currentSlug = this.getSlugFromPath(this.props.location.pathname);
		
		if (currentSlug && prevSlug !== currentSlug) {
			this.checkAndLoadRestaurant();
		}
	}

	componentWillUnmount() {
		// Clean up event listeners
		window.removeEventListener('locationChanged', this.handleLocationChange);
		window.removeEventListener('storage', this.handleStorageChange);
	}

	handleLocationChange = () => {
		console.log('SingleStoreRouter: Location change detected');
		this.reloadStoreConfig();
	};

	handleStorageChange = (e) => {
		if (e.key === 'userSetAddress' || (!e.key && this.hasLocationChanged())) {
			console.log('SingleStoreRouter: Storage location change detected');
			this.reloadStoreConfig();
		}
	};

	hasLocationChanged = () => {
		const userSetAddress = localStorage.getItem('userSetAddress');
		if (!userSetAddress) return false;
		
		try {
			const newLocation = JSON.parse(userSetAddress);
			const oldLocation = this.currentLocation;
			
			if (!oldLocation) return true;
			
			const coordsChanged = oldLocation.lat !== newLocation.lat || 
			                      oldLocation.lng !== newLocation.lng;
			
			return coordsChanged;
		} catch (e) {
			return false;
		}
	};

	reloadStoreConfig = () => {
		let slug = detectRestaurantSlug();
		
		// If at root and no slug detected, check for single brand for domain
		if (!slug && window.location.pathname === '/') {
			const singleBrand = localStorage.getItem('singleBrandForDomain');
			if (singleBrand) {
				slug = singleBrand;
			}
		}
		
		if (slug) {
			console.log('SingleStoreRouter: Reloading store config for', slug);
			
			// Update current location
			const userSetAddress = localStorage.getItem('userSetAddress');
			if (userSetAddress) {
				try {
					this.currentLocation = JSON.parse(userSetAddress);
					console.log('SingleStoreRouter: New location coordinates:', this.currentLocation);
				} catch (e) {
					console.error('SingleStoreRouter: Error parsing userSetAddress', e);
					this.currentLocation = null;
				}
			}
			
			// Clear manual restaurant selection when location changes
			localStorage.removeItem('selectedBrandRestaurantId');
			
			// Re-fetch store config with new coordinates
			console.log('SingleStoreRouter: Fetching nearest store based on new location...');
			this.props.getSingleStoreConfig(slug);
		}
	};

	getSlugFromPath = (pathname) => {
		const segments = pathname.split('/').filter(seg => seg);
		return segments[0] || null;
	};

	checkAndLoadRestaurant = () => {
		let slug = detectRestaurantSlug();
		
		// If at root and no slug detected, check for single brand for domain
		if (!slug && window.location.pathname === '/') {
			const singleBrand = localStorage.getItem('singleBrandForDomain');
			if (singleBrand) {
				slug = singleBrand;
				console.log('SingleStoreRouter: Using single brand for domain:', slug);
			}
		}
		
		if (slug) {
			// Store the slug in localStorage and Redux
			storeRestaurantSlug(slug);
			
			if (slug !== this.props.restaurantSlug) {
				this.props.setRestaurantSlug(slug);
				
				// Validate brand exists via API call
				// console.log(`Validating brand: ${slug}`);
				const configPromise = this.props.getSingleStoreConfig(slug);
				
				if (configPromise && configPromise.then) {
					configPromise.then((response) => {
						// Brand is valid and exists
						// console.log(`✅ Brand "${slug}" validated successfully`);
						this.setState({ initialLoadComplete: true });
					}).catch((error) => {
						// Brand doesn't exist - redirect to valid brand or selection
						// console.error(`❌ Brand "${slug}" does not exist or API error:`, error);
						
						// Clear invalid brand from localStorage
						localStorage.removeItem('lastVisitedBrand');
						localStorage.removeItem('restaurantSlug');
						
						// Check if there's a previously valid brand to redirect to
						const lastValidBrand = localStorage.getItem('lastValidBrand');
						
						if (lastValidBrand && lastValidBrand !== slug) {
							// Redirect to last known valid brand
							// console.log(`Redirecting to last valid brand: ${lastValidBrand}`);
							window.location.href = `/${lastValidBrand}`;
						} else {
							// No valid brand - go to brand selection
							// console.log('Redirecting to brand selection page');
							window.location.href = '/';
						}
						
						this.setState({ initialLoadComplete: true });
					});
				} else {
					// If not a promise, mark as complete immediately
					this.setState({ initialLoadComplete: true });
				}
			} else {
				// If slug is same, mark as complete
				this.setState({ initialLoadComplete: true });
			}
		} else {
			this.setState({ initialLoadComplete: true });
		}
	};

	render() {
		const { children, currentRestaurant } = this.props;
		const { initialLoadComplete } = this.state;
		const slug = detectRestaurantSlug();
		const pathname = this.props.location.pathname;
		
		// Only show loading on the initial brand home page load
		// Don't block navigation to other pages like cart, account, etc.
		const isBrandHomePage = pathname === `/${slug}` || pathname === `/${slug}/`;
		
		if (slug && !initialLoadComplete && isBrandHomePage && !currentRestaurant) {
			return <Loading />;
		}

		// Render children for all other pages
		return <>{children}</>;
	}
}

const mapStateToProps = (state) => ({
	loading: state.singlestore.loading,
	currentRestaurant: state.singlestore.currentRestaurant,
	restaurantSlug: state.singlestore.restaurantSlug,
	brand: state.singlestore.brand,
	hasMultipleLocations: state.singlestore.hasMultipleLocations,
});

export default connect(mapStateToProps, { getSingleStoreConfig, setRestaurantSlug })(
	withRouter(SingleStoreRouter)
);

